import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card, Table, Pagination, Button, Spinner, Form } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import { getTripReportList } from "../../redux/actions/VerifiedReportActions";
import { getMasterDriverList, getMasterVehicleList, getMasterVendorList, getAllUsersRoleWiseList } from "../../redux/actions/MasterActions";
import RoundedPagination from "../../Components/Paginate";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-dropdown-select";
import { logout } from "../../redux/actions/AuthActions";
import useDebounce from "../../hooks/useDebounce";
import { getDisplayDateTimeFormat } from '../../util/generalUtills';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { CSVLink } from 'react-csv';
import DatePicker from "react-datepicker";
import { addDays } from "date-fns";
import Preloader from "../../Components/Preloader";
import PrintTable from "../../Components/PrintTable";
import ExportToExcel from "../../Components/ExportToExcel";


const Dashboard = () => {
  const { t } = useTranslation();
  const tableRef = useRef();
  const [swalProps, setSwalProps] = useState({});
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();
  const {
    AuthReducer: { auth, errors, loading },
    verifiedReportReducer: {
      tripReportLists,
      reportPermissions,
      loadingReport,
      errorsReport,
      reportCount,
    },
    masterReducer: {
      masterVehicleLists,
      masterDriverLists,
      masterVendorLists,
      masterUsersRoleWiseLists,
      loadingMaster,
      errorsMaster,
    },
  } = useSelector((state) => state);
  const [page, setPage] = useState(1);
  const [paginate, setPaginate] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState('desc');
  const indexOfLastItem = (page + 1) * paginate;
  const indexOfFirstItem = indexOfLastItem - paginate;
  const [jenId, setJenId] = useState("");
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const [vendorId, setVendorId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (auth?.accessToken) {
      dispatch(getAllUsersRoleWiseList(auth?.accessToken, 'JUNIOR_ENGINEER'));
      dispatch(getMasterVendorList(auth?.accessToken, jenId));
    }
  }, [auth?.accessToken, jenId, dispatch]);

  useEffect(() => {
    if (jenId && auth?.accessToken) {
      dispatch(getMasterVehicleList(auth?.accessToken, jenId, vendorId));
      dispatch(getMasterDriverList(auth?.accessToken, jenId, vendorId));
    }
  }, [jenId, vendorId, auth?.accessToken, dispatch])


  const fetchTripReportList = useCallback(() => {
    if (auth?.accessToken) {
      dispatch(getTripReportList(auth?.accessToken, paginate, page, search, sort, jenId, vendorId, vehicleId, driverId, startDate, endDate));
    }
  }, [auth?.accessToken, paginate, page, search, sort, dispatch]);

  useDebounce(fetchTripReportList, 500, [auth, paginate, page, search, sort, jenId, vendorId, vehicleId, driverId, startDate, endDate]);

  const csvHead = [['SR No.(1)', 'Booking ID(2)', 'Pickup Address(3)', 'Drop Address(4)',
    'Booking Date(5)', 'Name/Mobile(6)', 'Booking Status(7)', 'Tanker Number(8)',
    'Remark(9)', 'Distance(10)', 'Amount(11)', 'Trip Time(12)']
  ]
  const csvData = tripReportLists.map((cv, index) => {
    const bookingLogs = cv?.bookingLogsBookingDetails || [];
    const waterSupplyStartLog = bookingLogs.find(log => log.status === 'ON GOING');
    const waterSupplyEndLog = bookingLogs.find(log => log.status === 'Water Supply End');
    const waterSupplyStart = waterSupplyStartLog ? getDisplayDateTimeFormat(waterSupplyStartLog.createdAt) : '';
    const waterSupplyEnd = waterSupplyEndLog ? getDisplayDateTimeFormat(waterSupplyEndLog.createdAt) : '';

    return [
      index + 1,
      cv?.bookingId,
      cv?.hydrantIdBookingDetailsHydrant?.address || '',
      cv?.destinationIdBookingDetailsDestination?.address || '',
      getDisplayDateTimeFormat(cv?.bookingDate) || '',
      `${cv?.name || ''}/${cv?.mobile || ''}`,
      cv?.taxiStatus || '',
      cv?.vehicle_number || '',
      cv?.aen_remark || '',
      cv?.totalDistance || '',
      cv?.totalTripAmount || '',
      `${waterSupplyStart} || ${waterSupplyEnd}`
    ];
  });
  const exportData = csvHead.concat(csvData);

  useEffect(() => {
    if (errors) {
      if (errors?.status === 401) {
        dispatch(logout())
        toast.error(errors?.data?.message);
        navigate('/login');
        window.location.reload();
      }
    }

  }, [errors])



  const handleSubmit = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);
    dispatch(getTripReportList(auth?.accessToken, paginate, page, search, sort, jenId, vendorId, vehicleId, driverId, startDate, endDate));
  };

  const handlePaginateChange = (e) => {
    const value = e.target.value === "All" ? reportCount : parseInt(e.target.value);
    setPaginate(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const transformedVendorOptions = useMemo(() =>
    masterVendorLists?.map(vendor => ({
      ...vendor,
      vendorId: vendor.vendorId || vendor.id,
      vendorName: vendor.vendorName || vendor.name
    })) || [],
    [masterVendorLists]
  );

  const transformedVehicleOptions = useMemo(() =>
    masterVehicleLists?.map(vehicle => ({
      ...vehicle,
      vehicleId: vehicle.id || vehicle.vehicleId,
      vehicleNumber: vehicle.vehicle_number || vehicle.vehicleNumber
    })) || [],
    [masterVehicleLists]
  );

  const transformedDriverOptions = useMemo(() =>
    masterDriverLists?.map(driver => ({
      ...driver,
      driverId: driver.id || driver.driverId,
      driverName: driver.driver_name || driver.driverName
    })) || [],
    [masterDriverLists]
  );

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: errors?.data?.state_code ? 'red' : base.borderColor,
      '&:hover': {
        borderColor: errors?.data?.state_code ? 'red' : base.borderColor,
      }
    }),
  };

  const printTable = () => {
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><style type="text/css">' +
      '#backgroundImage{' +
      'min-height: 50vh;' +
      'position: relative;' +
      'margin: 0;' +
      '-webkit-print-color-adjust: exact !important; ' +
      'color-adjust: exact !important;' +
      '}' +
      '#backgroundImage:before {' +
      'content: "";' +
      'position: absolute;' +
      'z-index: -1;' +
      'top: 0;' +
      'bottom: 0;' +
      'left: 0;' +
      'right: 0;' +
      'opacity: 0.04;' +
      'background:' +
      'url(\'https://phedr.consoletrack.com/logo.jpg\')' +
      '}' +
      '</style></head><body onload="window.print()" id="backgroundImage"><div align="center ">' + document.title + '</div><div class="main1"><table border="1" cellspacing="0">');
    printWindow.document.write(document.getElementById('table-to-print').innerHTML);
    printWindow.document.write('</table></div><div align="center">This is a system generated report it does not require any signature or stamp</div><div align="right"><img src="logo.jpg" style="opacity: 0.1"></div></body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: t("Trip Report"), path: "/Trip-Report", active: true },
        ]}
        title={t("Trip Report")}
      />

      <Row>
        <Col>
          {(reportPermissions?.filter) && (
            <>
              <Card>
                <Card.Body>
                  <Row>
                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("J.E.N.")}</Form.Label>
                        <Select
                          options={masterUsersRoleWiseLists || []}
                          values={masterUsersRoleWiseLists?.filter(name => name.id === jenId) || []}
                          name="jen_id"
                          labelField={"name"}
                          valueField={"id"}
                          searchable={true}
                          searchBy="name"
                          dropdownHeight="300px"
                          separator={true}
                          onChange={(selectedData) => {
                            setJenId(selectedData[0]?.id || "");
                            setVendorId(""); // Reset vendor when JEN changes
                            setVehicleId(""); // Reset vehicle when JEN changes
                            setDriverId(""); // Reset driver when JEN changes
                          }}
                          styles={customSelectStyles}
                        />
                        {errorsMaster?.data?.jen_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.jen_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Vendor")}</Form.Label>
                        <Select
                          options={transformedVendorOptions}
                          values={transformedVendorOptions?.filter(name => name.vendorId === vendorId) || []}
                          name="vendor_id"
                          labelField={"vendorName"}
                          valueField={"vendorId"}
                          searchBy={"vendorName"}
                          onChange={(selectedData) => {
                            setVendorId(selectedData[0]?.vendorId || "");
                            setVehicleId(""); // Reset vehicle when vendor changes
                            setDriverId(""); // Reset driver when vendor changes
                          }}
                          styles={customSelectStyles}
                        />

                        {errorsMaster?.data?.vendor_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.vendor_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Vehicle")}</Form.Label>
                        <Select
                          options={transformedVehicleOptions}
                          values={transformedVehicleOptions?.filter(option => option.vehicleId === vehicleId) || []}
                          name="vehicle_id"
                          labelField={"vehicleNumber"}
                          valueField={"vehicleId"}
                          searchBy={"vehicleNumber"}
                          onChange={(selectedData) => setVehicleId(selectedData[0]?.vehicleId || "")}
                          disabled={!jenId}
                          styles={customSelectStyles}
                        />

                        {errorsMaster?.data?.vehicle_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.vehicle_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Driver")}</Form.Label>
                        <Select
                          options={transformedDriverOptions}
                          values={transformedDriverOptions?.filter(option => option.driverId === driverId) || []}
                          name="driver_id"
                          labelField={"driverName"}
                          valueField={"driverId"}
                          searchBy={"driverName"}
                          onChange={(selectedData) => setDriverId(selectedData[0]?.driverId || "")}
                          disabled={!jenId}
                          styles={customSelectStyles}
                        />

                        {errorsMaster?.data?.driver_id && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.driver_id}</p>
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col lg={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Date")}</Form.Label>
                        <DatePicker
                          selectsRange={true}
                          dateFormat="yyyy-MM-dd"
                          startDate={startDate}
                          endDate={endDate}
                          maxDate={addDays(new Date(), 1)}
                          todayButton="Today"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          className="form-control"
                          onChange={(update) => {
                            if (update.length === 2) {
                              const date1 = new Date(update[0]);
                              date1.setHours(12, 0, 0, 0);
                              setDateRange([date1, null]);
                              if (update[1] != null) {
                                const date2 = new Date(update[1] || update[0]);
                                date2.setHours(12, 0, 0, 0);
                                setDateRange([date1, date2]);
                              }
                            }
                          }}
                          withPortal
                        />
                        {errorsMaster?.data?.startDate && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.startDate}</p>
                          </div>
                        )}
                        {errorsMaster?.data?.endDate && (
                          <div className="invalid-feedback d-block">
                            <p className="text-danger">{errorsMaster?.data?.endDate}</p>
                          </div>
                        )}
                      </Form.Group>

                    </Col>

                    <Col lg={2} >
                      <div className="mb-3" style={{ marginTop: '1.6rem' }}>
                        {loadingReport ?
                          <Button color="primary" disabled>
                            <Spinner
                              className="spinner-border-sm me-1"
                              tag="span"
                              color="white"
                            />
                            Please wait ...
                          </Button>
                          :
                          <Button type="button" onClick={handleSubmit} disabled={loadingReport}>
                            Submit
                          </Button>
                        }
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </>
          )}

          <Card>
            <Card.Body>
              <div className="mb-2">
                <Row>
                  <Col lg={3}>
                    {(reportPermissions?.filter) && (
                      <>
                        <div className="d-flex align-items-center">
                          <input
                            type="search"
                            placeholder="Search..."
                            className="form-control ms-1"
                            autoComplete="off"
                            value={search}
                            onChange={handleSearchChange}
                          />
                        </div>
                      </>
                    )}
                  </Col>
                  <Col lg={9}>
                    <div className="d-flex justify-content-end align-items-right">
                      <Col lg={3}>
                        <Row>
                          <Col lg={2}></Col>
                          {(reportPermissions?.print) && (
                            <>
                              <Col lg={5}>
                                <PrintTable
                                  tableId="table-to-print"
                                  logoUrl="https://admin.brainbird.co.in/logo.jpg"
                                  title=""
                                  removeLastColumn={true}
                                />
                              </Col>
                            </>
                          )}

                          {(reportPermissions?.excel) && (
                            <>
                              <Col lg={5}>
                                <ExportToExcel tableRef={tableRef} filename="data.xlsx" />
                              </Col>
                            </>
                          )}
                        </Row>
                      </Col>
                    </div>
                  </Col>
                </Row>
              </div>

              <div style={{ overflowY: 'auto', maxHeight: '500px', border: "1px solid #dee2e6" }}>
                <Table ref={tableRef} className="mb-0 table-bordered"
                  size="sm"
                  id="table-to-print"
                  striped
                  style={{ width: "100%", tableLayout: "fixed" }}>
                  <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1 }}>
                    <tr>
                      <th>SR No.(1)</th>
                      <th>Booking ID(2)</th>
                      <th>Pickup Address(3)</th>
                      <th>Drop Address(4)</th>
                      <th>Booking Date(5)</th>
                      <th>Name/Mobile(6)</th>
                      <th>Booking Status(7)</th>
                      <th>Tanker Number(8)</th>
                      <th>Remark(9)</th>
                      <th>Distance(10)</th>
                      <th>Travelled(10)</th>
                      <th>Water (K.L.)(10)</th>
                      <th>Amount(11)</th>
                      <th>Trip Time(12)</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tripReportLists?.length === 0 ? (
                      <tr>
                        <td colSpan="13">No data found</td>
                      </tr>
                    ) : (
                      tripReportLists?.map?.((item, index) => {
                        // const bookingLogs = item?.bookingLogsBookingDetails || [];
                        const waterSupplyStartLog = item.startTime;
                        const waterSupplyEndLog = item.endTime;
                        const waterSupplyStart = waterSupplyStartLog ? getDisplayDateTimeFormat(waterSupplyStartLog) : '';
                        const waterSupplyEnd = waterSupplyEndLog ? getDisplayDateTimeFormat(waterSupplyEndLog) : '';
                        return (
                          <tr key={item.id}>
                            <th scope="row">{index + 1 + (page - 1) * paginate}</th>
                            <td>{item?.bookingId}</td>
                            <td>{item?.hydrantName}</td>
                            <td>{item?.destinationName}</td>
                            <td>{getDisplayDateTimeFormat(item?.bookingDate)}</td>
                            <td>{item?.name}<br />{item?.mobile}</td>
                            <td>{item?.taxiStatus}</td>
                            <td>{item?.vehicleNumber}</td>
                            <td>{item?.jen?.remark}</td>
                            <td>{item?.totalDistance}</td>
                            <td>{item?.travelledDistance}</td>
                            <td>{item?.fillingVolume}</td>
                            <td>{item?.totalTripAmount}</td>
                            <td>{waterSupplyStart}<br />{waterSupplyEnd}</td>
                            <td>
                              <Link to={`/Track-Details-Booking/${item?.id}`} className="badge bg-soft-warning text-dark m-1"
                                target="_blank"
                                rel="noopener noreferrer">
                                Track
                              </Link>

                              <Link to={`/Download-Slip/${item?.id}`} className="badge bg-soft-info text-dark m-1"
                                target="_blank"
                                rel="noopener noreferrer">
                                Download Slip
                              </Link>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="mt-3 d-lg-flex align-items-center text-center pb-1">
                <div className="d-inline-block me-3">
                  <label className="me-1">Display :</label>
                  <select className="form-select d-inline-block w-auto" onChange={handlePaginateChange}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                <span className="me-3">Page <strong>{page}</strong> of <strong>{Math.ceil(reportCount / paginate)}</strong> </span>
                <RoundedPagination
                  currentPage={page}
                  totalPages={Math.ceil(reportCount / paginate)}
                  onPageChange={(pageNumber) => setPage(pageNumber)}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loadingMaster && (<Preloader />)}
      {loadingReport && (<Preloader />)}
    </>
  );
};

export default Dashboard;
